import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  Button,
  Grid,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import EmailIcon from "@mui/icons-material/Email";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import DownloadIcon from "@mui/icons-material/Download";
import {
  getSolDetail,
  processSingleResourceURL,
  postNote,
  deleteNote,
  updateTrackedSolicitations,
  updateArchivedSols,
  getAwardEntityInfo,
  getGeneratedDescription,
} from "../networking/DetailCalls";
import { processStringFromHTML } from "../utils/Helpers";
import { useAuth } from "../context/AuthContext";

const capitalizeWords = (str) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const Detail = () => {
  const location = useLocation();
  const theme = useTheme();
  const { currentUser } = useAuth();
  const initialSolicitation = location.state?.solicitation;
  const userID = location.state?.userID;
  const email = location.state?.email;
  const [solicitation, setSolicitation] = useState(initialSolicitation);
  const [solDetail, setSolDetail] = useState({ amendments: [], award: null });
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState("");
  const [storedResources, setStoredResources] = useState(
    initialSolicitation.storedResources || []
  );
  const [processingProgress, setProcessingProgress] = useState(null);
  const [isArchived, setIsArchived] = useState(false);
  const [openArchiveDialog, setOpenArchiveDialog] = useState(false);
  const [awardDescription, setAwardDescription] = useState("");
  const [generatedDescription, setGeneratedDescription] = useState("");
  const [openGeneratedDialog, setOpenGeneratedDialog] = useState(false);
  const [generatingDesc, setGeneratingDesc] = useState(false);

  useEffect(() => {
    const isAwarded = solicitation.isAwarded ? true : false;

    setNotes(solicitation.notes);

    if (solicitation.archivedSols?.includes(solicitation.solicitationNumber)) {
      setIsArchived(true);
    }

    if (solicitation.resourceLinks && solicitation.resourceLinks.length > 0) {
      handleResourceProcessing(
        solicitation.noticeId,
        solicitation.storedResources || [],
        solicitation.resourceLinks
      );
    }

    if (isAwarded) {
      getAwardEntityInfo(solicitation.noticeId)
        .then((data) => {
          if (data && data.award && data.entity) {
            const baseString =
              "Entity: " +
              data.entity.entityName +
              "<br>Entity ID: " +
              data.award.entityId +
              "<br>Address: " +
              data.entity.entityAddress +
              "<br>Amount: " +
              data.award.amount;

            const descText =
              solicitation.descriptionText &&
              !solicitation.descriptionText.includes("Error: ")
                ? solicitation.descriptionText
                : "";

            const finalText = descText
              ? baseString + "<br><br>" + descText
              : baseString;
            setAwardDescription(finalText);
          } else if (
            solicitation.descriptionText &&
            !solicitation.descriptionText.includes("Error: ")
          ) {
            setAwardDescription(solicitation.descriptionText);
          } else {
            setAwardDescription("");
          }
        })
        .catch((error) => {
          console.error("Error fetching award/entity info:", error);
          if (
            solicitation.descriptionText &&
            !solicitation.descriptionText.includes("Error: ")
          ) {
            setAwardDescription(solicitation.descriptionText);
          } else {
            setAwardDescription("");
          }
        });
    } else {
      if (
        solicitation.descriptionText &&
        !solicitation.descriptionText.includes("Error: ")
      ) {
        setAwardDescription(solicitation.descriptionText);
      } else {
        setAwardDescription("");
      }
    }

    getSolDetail(
      solicitation.noticeId,
      solicitation.solicitationNumber,
      isAwarded
    )
      .then((data) => {
        setSolDetail(data);
      })
      .catch((error) => {
        console.error("Error fetching solicitation detail:", error);
      });
  }, [solicitation]);

  const handleResourceProcessing = (noticeId, stored, links) => {
    setStoredResources(stored);

    if (stored.length < links.length) {
      setProcessingProgress(`Processing: ${stored.length}/${links.length}`);

      const missingIndexes = [...Array(links.length).keys()].slice(
        stored.length
      );
      missingIndexes.forEach((index) => {
        processSingleResourceURL(noticeId, links[index], index)
          .then((resource) => {
            setStoredResources((prevResources) => {
              const updatedResources = [...prevResources, resource].slice(
                0,
                links.length
              );
              if (updatedResources.length === links.length) {
                setProcessingProgress(null);
              } else {
                setProcessingProgress(
                  `Processing: ${updatedResources.length}/${links.length}`
                );
              }
              return updatedResources;
            });
          })
          .catch((error) =>
            console.error("Error processing resource URL:", error)
          );
      });
    }
  };

  const handleAddNote = () => {
    if (noteInput.trim() !== "") {
      const newNote = noteInput.trim();
      const solicitationId = solicitation.solicitationNumber;

      postNote(userID, solicitationId, newNote)
        .then((noteData) => {
          setNotes((prevNotes) => [...prevNotes, noteData]);
          setNoteInput("");
        })
        .catch((error) => console.error("Error adding note:", error));
    }
  };

  const handleDeleteNote = (noteId) => {
    deleteNote(noteId)
      .then(() => {
        setNotes((prevNotes) => prevNotes.filter((note) => note.id !== noteId));
      })
      .catch((error) => console.error("Error deleting note:", error));
  };

  const handleTrackUntrack = () => {
    const updatedIsSaved = !solicitation.isSaved;
    setSolicitation((prevSolicitation) => ({
      ...prevSolicitation,
      isSaved: updatedIsSaved,
    }));

    updateTrackedSolicitations(
      userID,
      solicitation.solicitationNumber,
      updatedIsSaved
    )
      .then(() => {
        console.log(
          updatedIsSaved
            ? `Solicitation ${solicitation.solicitationNumber} is now tracked.`
            : `Solicitation ${solicitation.solicitationNumber} is no longer tracked.`
        );
      })
      .catch((error) => {
        console.error("Error updating trackedSolicitations:", error);
      });
  };

  const handleShare = () => {
    const subject = "Solicitation Shared from Quantum CRM";
    const displayName =
      currentUser?.name ||
      currentUser?.displayName ||
      email ||
      "A Quantum user";

    const solicitationUrl = `https://app.quantum-crm.com/?noticeid=${encodeURIComponent(
      solicitation.noticeId
    )}`;
    const signupUrl = "https://welcome.quantum-crm.com";

    console.log(`[SHARE] using display name: ${displayName}`);

    const body = [
      "Join Quantum for Free",
      "",
      `Solicitation: ${solicitation.title}`,
      `From: ${displayName}`,
      "",
      "View the solicitation:",
      solicitationUrl,
      "",
      "Create your free Quantum account:",
      signupUrl,
      "",
      "— Sent from Quantum CRM",
    ].join("\n");

    const mailtoLink = `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    window.location.href = mailtoLink;
  };

  const handleShareFile = (fileUrl) => {
    const subject = "File Shared from Quantum CRM";
    const body = `View the following file from Quantum CRM.\n\n${fileUrl}\n\nhttps://quantum-crm.com/`;

    const mailtoLink = `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  const handleDownloadFile = (fileUrl, fileName) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleArchiveUnarchive = () => {
    setOpenArchiveDialog(true);
  };

  const confirmArchiveAction = () => {
    const newIsArchived = !isArchived;
    updateArchivedSols(userID, solicitation.solicitationNumber, newIsArchived)
      .then(() => {
        setIsArchived(newIsArchived);
        console.log(
          newIsArchived
            ? `Solicitation ${solicitation.solicitationNumber} is now archived.`
            : `Solicitation ${solicitation.solicitationNumber} is no longer archived.`
        );
        setOpenArchiveDialog(false);
      })
      .catch((error) => {
        console.error("Error updating archivedSols:", error);
        setOpenArchiveDialog(false);
      });
  };

  const handleAmendmentSelect = (amendment) => {
    getSolDetail(
      amendment.noticeId,
      amendment.solicitationNumber,
      amendment.isAwarded
    )
      .then((newSolDetail) => {
        setSolicitation({
          ...amendment,
          notes: solicitation.notes,
          hasNotes: solicitation.hasNotes,
        });

        setSolDetail(newSolDetail);
        setStoredResources(amendment.storedResources || []);
      })
      .catch((error) => {
        console.error("Error fetching amendment detail:", error);
      });
  };

  const handleOpenFile = (fileUrl, fileName) => {
    const fileExtension = fileName.split(".").pop().toLowerCase();

    if (fileExtension === "csv" || fileExtension === "xlsx") {
      const googleSheetsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
        fileUrl
      )}&embedded=true`;
      window.open(googleSheetsUrl, "_blank");
    } else if (fileExtension === "docx") {
      const googleDriveUrl = `https://drive.google.com/viewerng/viewer?url=${encodeURIComponent(
        fileUrl
      )}&embedded=true`;
      window.open(googleDriveUrl, "_blank");
    } else if (fileExtension === "pdf") {
      const googleDocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
        fileUrl
      )}&embedded=true`;
      window.open(googleDocsUrl, "_blank");
    } else {
      window.open(fileUrl, "_blank");
    }
  };

  const handleGenerateDescription = () => {
    setGeneratingDesc(true);
    getGeneratedDescription(solicitation.noticeId)
      .then((desc) => {
        setGeneratedDescription(desc);
        setOpenGeneratedDialog(true);
      })
      .catch((error) => {
        console.error("Error generating description:", error);
      })
      .finally(() => {
        setGeneratingDesc(false);
      });
  };

  return (
    <Box sx={{ padding: 3 }}>
      {/* Header */}
      <Typography
        variant="h4"
        gutterBottom
        component="a"
        href={solicitation.uiLink}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ textDecoration: "none", color: "inherit" }}
      >
        {solicitation.title}
      </Typography>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2, marginTop: 2, marginBottom: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleTrackUntrack}
        >
          {solicitation.isSaved ? "Currently Tracking" : "Track Solicitation"}
        </Button>

        <Button variant="contained" color="secondary" onClick={handleShare}>
          Share
        </Button>

        {solicitation.uiLink && (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => window.open(solicitation.uiLink, "_blank")}
          >
            SAM.gov
          </Button>
        )}

        <Button
          variant="contained"
          color="error"
          sx={{ marginLeft: "auto" }}
          onClick={handleArchiveUnarchive}
        >
          {isArchived ? "Unarchive" : "Archive"}
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={openArchiveDialog}
        onClose={() => setOpenArchiveDialog(false)}
      >
        <DialogTitle>Confirm Archive Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {isArchived ? "unarchive" : "archive"} this
            solicitation?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenArchiveDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmArchiveAction} color="primary" autoFocus>
            Yes, {isArchived ? "Unarchive" : "Archive"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openGeneratedDialog}
        onClose={() => setOpenGeneratedDialog(false)}
      >
        <DialogTitle>Generated Description</DialogTitle>
        <DialogContent>
          <DialogContentText>{generatedDescription}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGeneratedDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Grid Layout */}
      <Grid container spacing={3} sx={{ marginBottom: 3 }}>
        {/* Solicitation Info Table (Full width) */}
        <Grid item xs={12}>
          <Paper
            sx={{
              padding: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Table sx={{ flexGrow: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Solicitation Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Contracting Office
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Posted Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Set Aside Description
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>NAICS Code</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{solicitation.solicitationNumber}</TableCell>
                  <TableCell>
                    {solicitation.contractingOffice
                      .split(".")
                      .map((line, index) => (
                        <span key={index}>
                          {line}
                          <br />
                        </span>
                      ))}
                  </TableCell>
                  <TableCell>
                    {solicitation.performanceCity
                      ? capitalizeWords(solicitation.performanceCity)
                      : "N/A"}
                    ,{" "}
                    {solicitation.performanceCountry
                      ? solicitation.performanceCountry.toUpperCase()
                      : "N/A"}
                    {solicitation.performanceZipCode
                      ? " " + solicitation.performanceZipCode
                      : ""}
                  </TableCell>
                  <TableCell>
                    {solicitation.isAwarded ? "Award" : solicitation.baseType}
                  </TableCell>
                  <TableCell>{solicitation.postedDate}</TableCell>
                  <TableCell>
                    {solicitation.setAsideDescription?.trim() || "None Listed"}
                  </TableCell>
                  <TableCell>{solicitation.naicsCode}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Left column: Notes, Additional Resources, and Amendments */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Notes Section */}
            <Paper
              sx={{
                padding: 2,
                minHeight: "200px",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Notes
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}
              >
                <TextField
                  fullWidth
                  label="Note to add..."
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                />
                <IconButton
                  onClick={handleAddNote}
                  color="primary"
                  sx={{ marginLeft: 1 }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
              <Table size="small">
                <TableBody>
                  {notes.map((note, index) => (
                    <TableRow key={index}>
                      <TableCell>{note.note}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handleDeleteNote(note.id)}
                          color="error"
                        >
                          <ClearIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            {/* Additional Resources */}
            <Paper
              sx={{
                padding: 2,
                minHeight: "200px",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Additional Resources
              </Typography>
              {solicitation.resourceLinks?.length > 0 ? (
                <Box>
                  {processingProgress && (
                    <Typography variant="body2">
                      {processingProgress}
                    </Typography>
                  )}
                  <Table size="small">
                    <TableBody>
                      {storedResources.map((resource, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box>
                              <Typography>
                                File: {resource.readableName}
                              </Typography>
                              <Box sx={{ display: "flex", gap: 1, mt: 0 }}>
                                <Tooltip title="Open file">
                                  <IconButton
                                    onClick={() =>
                                      handleOpenFile(
                                        resource.url,
                                        resource.readableName
                                      )
                                    }
                                    sx={{ color: "secondary.main" }}
                                  >
                                    <FindInPageIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Share file">
                                  <IconButton
                                    onClick={() =>
                                      handleShareFile(resource.url)
                                    }
                                    sx={{ color: "secondary.main" }}
                                  >
                                    <EmailIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download file">
                                  <IconButton
                                    onClick={() =>
                                      handleDownloadFile(
                                        resource.url,
                                        resource.readableName
                                      )
                                    }
                                    sx={{ color: "secondary.main" }}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <Typography>N/A</Typography>
              )}
            </Paper>

            {/* Amendments */}
            <Paper
              sx={{
                padding: 2,
                minHeight: "200px",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Amendments
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Posted Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Posted Files
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Response Date
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {solDetail.amendments?.map((amendment, index) => (
                    <TableRow
                      key={index}
                      onClick={() => handleAmendmentSelect(amendment)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: theme.palette.custom.surface,
                      }}
                    >
                      <TableCell>{amendment.postedDate}</TableCell>
                      <TableCell>
                        {amendment.resourceLinks?.length || 0}
                      </TableCell>
                      <TableCell>
                        {amendment.responseDeadline || "N/A"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        </Grid>

        {/* Right column: Description */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ padding: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Description
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleGenerateDescription}
                disabled={generatingDesc}
              >
                Generate (BETA)
              </Button>
            </Box>
            <Typography>
              {awardDescription && awardDescription.trim() !== ""
                ? processStringFromHTML(awardDescription)
                : "No additional information provided by SAM.gov."}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Detail;
